/**
 * menu store - LNB 메뉴 트리(서버 FRM_MENU + 권한 업무기능 필터) 캐시.
 * 서버 실패면 PLAN_MENU 폴백(표시 전용 - 실제 데이터 강제는 서버 접근범위가 담당).
 */
import { defineStore } from 'pinia';
import http from '@/services/http';
import { PLAN_MENU, findCategoryKey } from '@/constants/menuTree';
import { useAuthStore } from '@/stores/auth';

// 서버는 운영사(플랫폼 제공자)가 아니면 트리에서 이 루트를 뺀다(MenuService)
export const DEV_ROOT = 'MN_DEV';

// 다른 탭·창에서 돌아올 때 다시 받는 최소 간격. 관리자가 권한을 바꾼 뒤 새로고침 없이 반영하기 위함(D-199)
const REFRESH_GAP_MS = 60 * 1000;
let visibilityHooked = false;

// 서버 트리 {menuId,label,route,icon,children[]} -> PLAN_MENU shape(MainLayout 소비 형태)
function toPlanShape(nodes) {
  return (nodes || []).map((cat) => ({
    key: cat.menuId,
    label: cat.label,
    icon: cat.icon || 'lnb-settings',
    groups: (cat.children || []).map((g) => ({
      key: g.menuId,
      label: g.label,
      children: (g.children || []).map((c) => ({ key: c.menuId, label: c.label, route: c.route || undefined })),
    })),
  }));
}

function collectRoutes(tree) {
  const set = new Set();
  (tree || []).forEach((cat) => (cat.groups || []).forEach((g) => (g.children || []).forEach((c) => {
    if (c.route) set.add(c.route);
  })));
  return set;
}

// 메뉴 카탈로그 전체 route(PLAN_MENU = 시드 원천과 1:1 동결) - 가드는 이 집합에 든 route 만 검사.
// 카탈로그 밖 route(홈·상세 하위 라우트)는 메뉴 권한 대상이 아니므로 통과.
// 단 meta.devTool 개발자 화면은 카탈로그 밖이라도 routeBlocked 가 개발자 루트 보유로 막는다.
export const CATALOG_ROUTES = collectRoutes(PLAN_MENU);

/** 메뉴 권한으로 막을 라우트인가. 서버 트리일 때만 판정한다(목·폴백 트리는 표시 전용). */
export function routeBlocked(menu, to) {
  if (!menu.fromServer || !to?.name) return false;
  if (CATALOG_ROUTES.has(to.name) && !menu.allowedRoutes.has(to.name)) return true;
  // 카탈로그 밖 개발자 화면(DEV0040·DEV0080)은 허용 목록에 없어 위 검사를 건너뛴다 - 개발자 루트를 받은 사용자만 연다.
  // 기준을 서버 가드(운영사 테넌트 + ADMIN)가 아니라 메뉴에 두는 것은 카탈로그 안 개발자 화면(DEV0020 등)과 같게 맞추기 위해서다
  return to.matched.some((r) => r.meta?.devTool) && !menu.tree.some((c) => c.key === DEV_ROOT);
}

// 조직장으로 들어온 사용자에게 다른 이름으로 보이는 메뉴(조직장 관리 -> 대리인 관리)
export const LEADER_MENU_LABELS = { ORM0030: '대리인 관리' };

/** 조직장이면 LEADER_MENU_LABELS 의 메뉴 이름을 바꾼 새 트리. 아니면 원래 트리를 돌려준다. */
export function relabelForLeader(tree, leader) {
  if (!leader) return tree;
  return (tree || []).map((cat) => ({
    ...cat,
    groups: (cat.groups || []).map((g) => ({
      ...g,
      children: (g.children || []).map((c) => (LEADER_MENU_LABELS[c.route] ? { ...c, label: LEADER_MENU_LABELS[c.route] } : c)),
    })),
  }));
}

// 인사 기준을 사용 안 함으로 끄면 쓸 수 없는 메뉴 - route -> 기준 유형(HRS0010 사용 설정)
// 승진발령기준은 「이 직급에서 저 직급으로」 규칙이라 직급으로만 돌아간다. 기준 데이터는 지우지 않는다
export const STD_MENUS = { CAM0012: 'GRADE' };

/** 사용 안 함 기준의 메뉴를 뺀 트리. 그 때문에 비게 된 묶음·대메뉴도 뺀다. 뺄 것이 없으면 원래 트리를 돌려준다. */
export function hideOffStdMenus(tree, isOff) {
  const hidden = new Set(Object.keys(STD_MENUS).filter((route) => isOff(STD_MENUS[route])));
  if (!hidden.size) return tree;
  return (tree || []).flatMap((cat) => {
    const groups = (cat.groups || []).flatMap((g) => {
      const children = (g.children || []).filter((c) => !hidden.has(c.route));
      return children.length || !(g.children || []).length ? [{ ...g, children }] : [];
    });
    return groups.length || !(cat.groups || []).length ? [{ ...cat, groups }] : [];
  });
}

/**
 * 왼쪽 메뉴의 대메뉴. 현재 화면의 대메뉴는 숨기기 전 트리에서 찾아 숨김 뒤에도 보이면 쓰고, 통째로 빠졌으면 보이는 첫 대메뉴.
 * 메뉴에 없는 화면(홈 등)이면 지금 선택이 보이는 한 그대로 둔다. 트리를 아직 받지 못했으면 지금 선택을 둔다.
 */
export function pickActiveCategory(tree, visible, routeName, current) {
  const shown = (key) => (visible || []).some((c) => c.key === key);
  const k = findCategoryKey(tree, routeName);
  if (k && shown(k)) return k;
  if (!k && current && shown(current)) return current;
  return visible?.[0]?.key || current || '';
}

export const useMenuStore = defineStore('menu', {
  state: () => ({
    tree: [],          // PLAN_MENU shape
    loaded: false,
    fromServer: false, // false = 폴백(가드 미적용, 표시 전용)
    userKey: '',       // 로드 당시 사용자(재로그인 시 자동 재로드)
    lastLoadedAt: 0,
    _pending: null,
  }),
  getters: {
    allowedRoutes: (s) => collectRoutes(s.tree),
  },
  actions: {
    async ensureLoaded() {
      const auth = useAuthStore();
      const key = String(auth.userId || auth.loginId || '');
      // 사용자가 바뀌면(재로그인) 이전 LNB 잔존 방지 위해 리셋 후 재로드
      if (this.loaded && this.userKey !== key) this.reset();
      if (this.loaded) return;
      if (this._pending) return this._pending;
      this.userKey = key;
      this._pending = this._load().finally(() => { this._pending = null; });
      return this._pending;
    },
    async _load() {
      try {
        const body = await http.get('/api/v1/menus');
        this.tree = toPlanShape(body?.data);
        this.fromServer = true;
        this.loaded = true;
        this.lastLoadedAt = Date.now();
        this._hookVisibility();
      } catch (e) {
        console.warn('[menu] 서버 메뉴 로드 실패 — PLAN_MENU 폴백(표시 전용)', e);
        this.tree = PLAN_MENU;
        this.fromServer = false;
        this.loaded = true;
      }
    },
    /**
     * 서버 메뉴를 다시 받아 LNB·라우터 가드 기준을 맞춘다. 트리를 비우지 않고 교체하며, 실패하면 기존 메뉴를 유지한다.
     * 서버 트리를 받은 뒤에만 동작한다(폴백은 표시 전용). minGapMs 안에 다시 부르면 건너뛴다.
     */
    async refresh({ minGapMs = REFRESH_GAP_MS } = {}) {
      if (!this.loaded || !this.fromServer || this._pending) return;
      if (Date.now() - this.lastLoadedAt < minGapMs) return;
      this.lastLoadedAt = Date.now();
      this._pending = http.get('/api/v1/menus')
        .then((body) => { this.tree = toPlanShape(body?.data); })
        .catch((e) => { console.warn('[menu] 메뉴 다시 불러오기 실패 - 기존 메뉴 유지', e); })
        .finally(() => { this._pending = null; });
      return this._pending;
    },
    _hookVisibility() {
      if (visibilityHooked || typeof document === 'undefined') return;
      visibilityHooked = true;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.refresh();
      });
    },
    reset() {
      this.tree = [];
      this.loaded = false;
      this.fromServer = false;
      this.userKey = '';
      this.lastLoadedAt = 0;
      this._pending = null;
    },
  },
});
